import { useState, FormEvent, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    furigana: '',
    company: '',
    email: '',
    phone: '',
    message: '',
    honeypot: ''
  });
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [privacyError, setPrivacyError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://sinax.co.jp';
    
    const contactPageSchema = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "お問い合わせ | 株式会社SINAX",
      "description": "株式会社SINAXへのお問い合わせはこちらから。ITコンサルティング、PMO支援、SES事業に関するご相談を承ります。",
      "url": `${siteUrl}/contact`,
      "mainEntity": {
        "@type": "Organization",
        "name": "株式会社SINAX",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "神田須田町1丁目7番8号 VORT秋葉原Ⅳ 2F",
          "addressLocality": "千代田区",
          "addressRegion": "東京都",
          "postalCode": "101-0041",
          "addressCountry": "JP"
        },
        "email": "info@sinax.co.jp"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(contactPageSchema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // スパム対策：honeypotフィールドに値が入っている場合は送信しない
    if (formData.honeypot) {
      return;
    }

    if (!privacyAgreed) {
      setPrivacyError(true);
      const el = document.getElementById('privacy-section');
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 120;
        window.scrollTo({ top, behavior: 'smooth' });
      }
      return;
    }

    setPrivacyError(false);

    // バリデーション
    if (!formData.name || !formData.furigana || !formData.email || !formData.message) {
      setSubmitStatus('error');
      return;
    }

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.currentTarget;
      const formDataToSend = new FormData(formElement);

      const response = await fetch('https://readdy.ai/api/form/d56d7b8f3qcgnii4i61g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataToSend as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          furigana: '',
          company: '',
          email: '',
          phone: '',
          message: '',
          honeypot: ''
        });
        setPrivacyAgreed(false);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('送信エラー:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-32 pb-24 px-6 lg:px-16">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="font-serif text-5xl lg:text-6xl text-darkgray mb-6">
              お問い合わせ
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              ご質問やご相談がございましたら、お気軽にお問い合わせください。
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-gray-50 rounded-lg p-8 lg:p-12"
          >
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit}>
              <div className="space-y-8">
                {/* Honeypot（スパム対策用の非表示フィールド） */}
                <input
                  type="text"
                  name="honeypot"
                  value={formData.honeypot}
                  onChange={handleChange}
                  style={{ display: 'none' }}
                  tabIndex={-1}
                  autoComplete="off"
                />

                {/* 名前 */}
                <div>
                  <label htmlFor="name" className="block text-base font-medium text-darkgray mb-3">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-5 py-4 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mustard focus:border-transparent transition-all"
                    placeholder="山田 太郎"
                  />
                </div>

                {/* ふりがな */}
                <div>
                  <label htmlFor="furigana" className="block text-base font-medium text-darkgray mb-3">
                    ふりがな <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="furigana"
                    name="furigana"
                    value={formData.furigana}
                    onChange={handleChange}
                    required
                    className="w-full px-5 py-4 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mustard focus:border-transparent transition-all"
                    placeholder="やまだ たろう"
                  />
                </div>

                {/* 会社名 */}
                <div>
                  <label htmlFor="company" className="block text-base font-medium text-darkgray mb-3">
                    会社名
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-5 py-4 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mustard focus:border-transparent transition-all"
                    placeholder="株式会社〇〇"
                  />
                </div>

                {/* メールアドレス */}
                <div>
                  <label htmlFor="email" className="block text-base font-medium text-darkgray mb-3">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-5 py-4 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mustard focus:border-transparent transition-all"
                    placeholder="example@email.com"
                  />
                </div>

                {/* 電話番号 */}
                <div>
                  <label htmlFor="phone" className="block text-base font-medium text-darkgray mb-3">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-5 py-4 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mustard focus:border-transparent transition-all"
                    placeholder="03-1234-5678"
                  />
                </div>

                {/* お問い合わせ内容 */}
                <div>
                  <label htmlFor="message" className="block text-base font-medium text-darkgray mb-3">
                    お問い合わせ内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={8}
                    className="w-full px-5 py-4 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mustard focus:border-transparent transition-all resize-none"
                    placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                  />
                  <p className="text-sm text-gray-500 mt-2 text-right">
                    {formData.message.length} / 500文字
                  </p>
                </div>

                {/* 個人情報利用目的・同意セクション */}
                <div
                  id="privacy-section"
                  className={`rounded-lg border p-6 transition-colors duration-200 ${
                    privacyError
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <h3 className="font-sans text-sm font-semibold text-darkgray tracking-wide mb-3">
                    個人情報利用目的
                  </h3>
                  <p className="font-sans text-sm text-gray-600 leading-relaxed mb-5">
                    ご入力いただいた個人情報の利用目的、個人情報お問い合わせ窓口等につきましては、当社の
                    <Link
                      to="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-darkgray underline underline-offset-2 hover:text-warmgray transition-colors duration-200 mx-0.5"
                    >
                      プライバシーポリシー
                    </Link>
                    をご参照ください。<br className="hidden sm:block" />
                    内容をご確認のうえ、ご同意いただける場合は、「個人情報の取り扱いに同意する」にチェックをしてください。
                  </p>

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <div className="relative flex-shrink-0 mt-0.5">
                      <input
                        type="checkbox"
                        id="privacy-agree"
                        checked={privacyAgreed}
                        onChange={(e) => {
                          setPrivacyAgreed(e.target.checked);
                          if (e.target.checked) setPrivacyError(false);
                        }}
                        className="sr-only"
                      />
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                          privacyAgreed
                            ? 'bg-darkgray border-darkgray'
                            : privacyError
                            ? 'border-red-400 bg-white'
                            : 'border-gray-400 bg-white group-hover:border-darkgray'
                        }`}
                      >
                        {privacyAgreed && (
                          <i className="ri-check-line text-white text-xs w-3 h-3 flex items-center justify-center" />
                        )}
                      </div>
                    </div>
                    <span className="font-sans text-sm text-darkgray leading-relaxed select-none">
                      個人情報の取り扱いに同意する
                      <span className="text-red-500 ml-1">*</span>
                    </span>
                  </label>

                  {privacyError && (
                    <motion.p
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-3 font-sans text-sm text-red-600 flex items-center gap-1.5"
                    >
                      <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center" />
                      個人情報の取り扱いへの同意が必要です。
                    </motion.p>
                  )}
                </div>

                {/* 送信ボタン */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    onClick={() => {
                      if (!privacyAgreed) setPrivacyError(true);
                    }}
                    className={`w-full whitespace-nowrap px-8 py-5 text-lg font-medium rounded-lg transition-all duration-300 ${
                      privacyAgreed
                        ? 'bg-mustard text-white cursor-pointer hover:bg-opacity-90'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isSubmitting ? '送信中...' : '送信する'}
                  </button>
                  {!privacyAgreed && (
                    <p className="text-center text-xs text-gray-400 mt-2">
                      個人情報の取り扱いに同意してから送信できます
                    </p>
                  )}
                </div>

                {/* ステータスメッセージ */}
                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-5 bg-green-50 border border-green-200 rounded-lg text-green-800 text-base text-center"
                  >
                    お問い合わせありがとうございます。担当よりご連絡します。
                  </motion.div>
                )}

                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-5 bg-red-50 border border-red-200 rounded-lg text-red-800 text-base text-center"
                  >
                    送信に失敗しました。必須項目をご確認の上、もう一度お試しください。
                  </motion.div>
                )}
              </div>
            </form>
          </motion.div>

          {/* 会社情報 */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-16 text-center"
          >
            <h2 className="font-serif text-3xl text-darkgray mb-8">会社情報</h2>
            <div className="space-y-4 text-base text-gray-700">
              <p>
                <strong>株式会社SINAX</strong>
              </p>
              <p>
                〒101-0041<br />
                東京都千代田区神田須田町1丁目7番8号　VORT秋葉原Ⅳ 2Ｆ
              </p>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
